import { Component } from '@theme/component';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
  SlideshowSelectEvent,
} from '@theme/events';

/**
 * @typedef {object} VideoSyncState
 * @property {string} mediaId
 * @property {HTMLVideoElement} [mainVideo]
 * @property {HTMLVideoElement} [thumbVideo]
 * @property {HTMLIFrameElement} [mainIframe]
 * @property {HTMLIFrameElement} [thumbIframe]
 * @property {() => void} [onTimeUpdate]
 */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {VideoSyncState | null} */
  #activeVideoSync = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupProductVideoSync(signal);

    queueMicrotask(() => {
      const slide = this.#getCurrentSlide();
      if (slide) this.#autoplayActiveSlideVideo(slide);
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stopVideoSync();
    this.#controller.abort();
  }

  /**
   * @param {AbortSignal} signal
   */
  #setupProductVideoSync(signal) {
    this.addEventListener('play', this.#handleVideoPlay, { capture: true, signal });
    this.addEventListener('pause', this.#handleVideoPause, { capture: true, signal });
    this.addEventListener('ended', this.#handleVideoEnded, { capture: true, signal });
    this.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStartedPlaying, { signal });
    this.addEventListener('deferred-media:paused', this.#handleDeferredMediaPaused, { signal });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideChange, { signal });
  }

  /**
   * @param {string} mediaId
   * @returns {HTMLVideoElement | null}
   */
  #getThumbnailVideo(mediaId) {
    const wrapper = this.querySelector(`.slideshow-controls__thumbnail-media--video[data-media-id="${mediaId}"]`);
    const video = wrapper?.querySelector('video.slideshow-controls__thumbnail-video');
    return video instanceof HTMLVideoElement ? video : null;
  }

  /**
   * @param {string} mediaId
   * @returns {HTMLIFrameElement | null}
   */
  #getThumbnailIframe(mediaId) {
    const iframe = this.querySelector(
      `iframe.slideshow-controls__thumbnail-video--iframe[data-media-id="${mediaId}"]`
    );
    return iframe instanceof HTMLIFrameElement ? iframe : null;
  }

  /**
   * @param {HTMLElement} target
   * @returns {string | undefined}
   */
  #getMediaId(target) {
    const fromProductMedia = target.closest('.product-media[data-media-id]');
    if (fromProductMedia instanceof HTMLElement && fromProductMedia.dataset.mediaId) {
      return fromProductMedia.dataset.mediaId;
    }

    const fromDeferred = target.closest('deferred-media');
    const container = fromDeferred?.closest('.product-media-container');
    const productMedia = container?.querySelector('.product-media[data-media-id]');
    if (productMedia instanceof HTMLElement && productMedia.dataset.mediaId) {
      return productMedia.dataset.mediaId;
    }

    return undefined;
  }

  /**
   * @param {HTMLIFrameElement} iframe
   * @param {string} message
   */
  #postToIframe(iframe, message) {
    iframe.contentWindow?.postMessage(message, '*');
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #playIframe(iframe) {
    if (iframe.dataset.videoType === 'youtube') {
      this.#postToIframe(iframe, '{"event":"command","func":"playVideo","args":""}');
    } else {
      this.#postToIframe(iframe, '{"method":"play"}');
    }
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #pauseIframe(iframe) {
    if (iframe.dataset.videoType === 'youtube') {
      this.#postToIframe(iframe, '{"event":"command","func":"pauseVideo","args":""}');
    } else {
      this.#postToIframe(iframe, '{"method":"pause"}');
    }
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #unmuteIframe(iframe) {
    if (iframe.dataset.videoType === 'youtube') {
      this.#postToIframe(iframe, '{"event":"command","func":"unMute","args":""}');
      this.#postToIframe(iframe, '{"event":"command","func":"setVolume","args":[100]}');
    } else {
      this.#postToIframe(iframe, '{"method":"setVolume","value":1}');
    }
  }

  /**
   * @param {string} mediaId
   * @param {HTMLVideoElement} mainVideo
   */
  #startHtmlVideoSync(mediaId, mainVideo) {
    const thumbVideo = this.#getThumbnailVideo(mediaId);
    if (!thumbVideo) return;

    this.#stopVideoSync();

    thumbVideo.muted = true;
    thumbVideo.loop = false;

    /** @type {() => void} */
    const onTimeUpdate = () => {
      if (mainVideo.paused || mainVideo.ended) return;

      const drift = Math.abs(thumbVideo.currentTime - mainVideo.currentTime);
      if (drift > 0.12) {
        try {
          thumbVideo.currentTime = mainVideo.currentTime;
        } catch {
          // Ignore seek errors while metadata is loading.
        }
      }

      if (thumbVideo.paused) {
        thumbVideo.play().catch(() => {});
      }
    };

    mainVideo.addEventListener('timeupdate', onTimeUpdate);
    onTimeUpdate();

    this.#activeVideoSync = { mediaId, mainVideo, thumbVideo, onTimeUpdate };
  }

  /**
   * @param {string} mediaId
   * @param {HTMLIFrameElement} mainIframe
   */
  #startIframeSync(mediaId, mainIframe) {
    const thumbIframe = this.#getThumbnailIframe(mediaId);
    if (!thumbIframe) return;

    this.#stopVideoSync();

    this.#unmuteIframe(mainIframe);
    this.#playIframe(mainIframe);
    this.#playIframe(thumbIframe);

    this.#activeVideoSync = { mediaId, mainIframe, thumbIframe };
  }

  #stopVideoSync() {
    if (!this.#activeVideoSync) return;

    const { mainVideo, thumbVideo, onTimeUpdate, mainIframe, thumbIframe } = this.#activeVideoSync;

    if (mainVideo && onTimeUpdate) {
      mainVideo.removeEventListener('timeupdate', onTimeUpdate);
    }

    if (thumbVideo) {
      thumbVideo.muted = true;
      thumbVideo.loop = true;
      thumbVideo.play().catch(() => {});
    }

    if (thumbIframe) {
      this.#playIframe(thumbIframe);
    }

    this.#activeVideoSync = null;
  }

  /** @param {Event} event */
  #handleVideoPlay = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLVideoElement) || !this.contains(target)) return;
    if (target.classList.contains('slideshow-controls__thumbnail-video')) return;
    if (!target.closest('.product-media-container')) return;

    const mediaId = this.#getMediaId(target);
    if (!mediaId) return;

    target.muted = false;
    target.volume = 1;

    this.#startHtmlVideoSync(mediaId, target);
  };

  /** @param {Event} event */
  #handleVideoPause = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLVideoElement) || !this.contains(target)) return;
    if (target.classList.contains('slideshow-controls__thumbnail-video')) return;

    if (this.#activeVideoSync?.mainVideo === target) {
      this.#stopVideoSync();
    }
  };

  /** @param {Event} event */
  #handleVideoEnded = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLVideoElement) || !this.contains(target)) return;
    if (target.classList.contains('slideshow-controls__thumbnail-video')) return;

    if (this.#activeVideoSync?.mainVideo === target) {
      this.#stopVideoSync();
    }
  };

  /** @param {Event} event */
  #handleMediaStartedPlaying = (event) => {
    if (!(event instanceof CustomEvent) || !event.detail?.resource) return;

    const resource = event.detail.resource;
    if (!(resource instanceof HTMLElement) || !this.contains(resource)) return;

    this.#stopVideoSync();

    const mediaId = this.#getMediaId(resource);
    if (!mediaId) return;

    const mainVideo = resource.querySelector('video');
    if (mainVideo instanceof HTMLVideoElement) {
      // HTML5 playback is handled by the bubbling `play` event (unmute + thumbnail sync).
      return;
    }

    const mainIframe = resource.querySelector('iframe[data-video-type]');
    if (mainIframe instanceof HTMLIFrameElement) {
      this.#startIframeSync(mediaId, mainIframe);
    }
  };

  /** @param {Event} event */
  #handleDeferredMediaPaused = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;

    if (this.#activeVideoSync?.mainVideo?.closest('deferred-media') === target) {
      this.#stopVideoSync();
      return;
    }

    if (this.#activeVideoSync?.mainIframe?.closest('deferred-media') === target) {
      this.#stopVideoSync();
    }
  };

  /**
   * @returns {Element | null}
   */
  #getCurrentSlide() {
    const slideshow = this.refs.slideshow;
    if (!slideshow?.slides?.length) return null;

    const index = typeof slideshow.current === 'number' ? slideshow.current : 0;
    return slideshow.slides[index] ?? null;
  }

  /**
   * @param {Element} slide
   */
  #autoplayActiveSlideVideo(slide) {
    const container = slide.querySelector(
      '.product-media-container--video, .product-media-container--external_video'
    );
    if (!container) return;

    const deferred = container.querySelector('deferred-media');
    if (!(deferred instanceof HTMLElement)) return;

    /** @type {{ loadContent?: (focus?: boolean) => void; playMedia?: () => void; isPlaying?: boolean }} */
    const mediaComponent = deferred;

    if (!deferred.hasAttribute('data-media-loaded')) {
      mediaComponent.loadContent?.(false);
    }

    requestAnimationFrame(() => {
      if (mediaComponent.isPlaying) return;
      mediaComponent.playMedia?.();
    });
  }

  #pauseAllGalleryVideos() {
    this.querySelectorAll('.product-media-container deferred-media').forEach((deferred) => {
      if (!(deferred instanceof HTMLElement)) return;

      if (typeof deferred.pauseMedia === 'function') {
        deferred.pauseMedia();
        return;
      }

      deferred.querySelector('video')?.pause();

      const iframe = deferred.querySelector('iframe[data-video-type]');
      if (iframe instanceof HTMLIFrameElement) {
        this.#pauseIframe(iframe);
      }
    });
  }

  /** @param {Event} event */
  #handleSlideChange = (event) => {
    this.#stopVideoSync();
    this.#pauseAllGalleryVideos();

    const slide =
      event instanceof SlideshowSelectEvent && event.detail?.slide instanceof Element
        ? event.detail.slide
        : this.#getCurrentSlide();

    if (slide) {
      this.#autoplayActiveSlideVideo(slide);
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
